import { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'src/components/snackbar';
import { useBoolean } from 'src/hooks/use-boolean';
import { useAxiosClientContext } from 'src/http/context/axios-client-context';
import { BenefitsRepository } from 'src/repositories/benefits/benefits.repository';
import { RestaurantsRepository } from 'src/repositories/stores/restaurants.repository';
import { StoresRepository } from 'src/repositories/stores/stores.repository';
import { useBenefitsStore } from 'src/stores/benefits/benefits.store';
import { useFilterStore } from 'src/stores/filters/filters.store';

export const useBenefits = () => {
  const { order, orderBy, page, limit, total, filters, setOrder, setOrderBy, setLimit, setPage, setTotal } = useFilterStore();
  const { setCategoriesOptions: setStoreCategoriesOptions, setLevelsOptions: setStoreLevelsOptions } = useBenefitsStore();
  const api = useAxiosClientContext();
  const [checked, setChecked] = useState<string[]>([]);
  const [benefits, setBenefits] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const useConfirmDeleteDialog = useBoolean(false);
  const [categories, setCategoriesLocal] = useState<any[]>([]);
  const [categoriesOptions, setCategoriesOptions] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [storesOptions, setStoresOptions] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [levelsOptions, setLevelsOptions] = useState<any[]>([]);
  const [rowToDelete, setRowToDelete] = useState<any>();
  const { toastWarning } = useSnackbar();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const tableHeader = [
    { label: 'Ativar', id: 'status', sortable: true },
    { label: 'Nome', id: 'name', sortable: true },
    { label: 'Tipo', id: 'type', sortable: true },
    { label: 'Categoria', id: 'category', sortable: true },
    { label: 'Loja', id: 'store', sortable: true },
    { label: 'Restaurante', id: 'restaurant', sortable: true },
    { label: 'Evento', id: 'event', sortable: true },
    { label: 'Nível', id: 'levels', sortable: true },
    { label: 'Prazo', id: 'endDate', sortable: true },
  ];

  const toggleExpandRow = (id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  };

  const getData = async () => {
    try {
      setIsLoading(true);
      const benefitsRepository = new BenefitsRepository(api);
      const response = await benefitsRepository.getBenefits({
        order,
        orderBy,
        page: page + 1,
        limit,
        ...filters,
      });

      setBenefits(response.value.data);
      setTotal(response.value.total);
      setChecked(response.value.data.filter((r: any) => r.status === 'active').map((r: any) => r.id));
    } catch (error) {
      setBenefits([]);
      setTotal(0);
      setChecked([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [order, orderBy, page, limit, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getCategories();
    getStores();
    getLevels();
    getRestaurants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getCategories = useCallback(async () => {
    try {
      const benefitsRepository = new BenefitsRepository(api);
      const response = await benefitsRepository.getCategories();
      if (response.isRight()) {
        const categoriesData = response.value?.data || response.value || [];
        const categories = categoriesData.map((cat: any) => ({
          id: cat._id?.$oid || cat._id || cat.id,
          name: cat.name || 'Categoria',
          label: cat.name || 'Categoria',
          value: cat._id?.$oid || cat._id || cat.id,
          type: cat.type || 'stores',
          ...cat,
        }));
        setCategoriesLocal(categories);
        setCategoriesOptions(categories);
        setStoreCategoriesOptions(categories);
      } else {
        console.error('Erro na resposta da API de categorias');
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, [api, setStoreCategoriesOptions]);

  const getStores = async () => {
    try {
      const storesRepository = new StoresRepository(api);
      const response = await storesRepository.getStores({ status: 'active' });
      if (response.isLeft()) {
        // Error handling - stores not loaded
      } else {
        const storesData = response.value.data.map((store: any) => ({
          id: store.id || store.id,
          label: store.name,
          value: store.id || store.id,
          name: store.name,
          ...store,
        }));

        setStores(storesData);
        setStoresOptions(storesData);
      }
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
    }
  };

  const getRestaurants = async () => {
    try {
      const restaurantsRepository = new RestaurantsRepository(api);
      const response = await restaurantsRepository.getRestaurants({ status: 'active' });
      if (response.isLeft()) {
        // Error handling - restaurants not loaded
      } else {
        const restaurantsData = response.value.data.map((restaurant: any) => ({
          id: restaurant.id || restaurant.id,
          label: restaurant.name,
          value: restaurant.id || restaurant.id,
          name: restaurant.name,
          ...restaurant,
        }));

        setRestaurants(restaurantsData);
      }
    } catch (error) {
      console.error('Erro ao carregar restaurantes:', error);
    }
  };

  const getLevels = async () => {
    try {
      const benefitsRepository = new BenefitsRepository(api);
      const response = await benefitsRepository.getLevels({ status: 'active' });

      const levelsData = response.value.data.map((level: any, index: number) => ({
        _id: level._id || level.id,
        label: level.label || level.level || level.name,
        value: level.value || level.level || level.name || level._id || level.id || `level-${index}`,
        name: level.name || level.level || level.label,
        level: level.level || level.name || level.label,
        promoteValue: level.promoteValue || 0,
        isActive: level.isActive !== undefined ? level.isActive : true,
        personMarkerCategoryID: level.personMarkerCategoryID,
        ...level,
      }));

      setLevels(levelsData);
      setLevelsOptions(levelsData);
      setStoreLevelsOptions(levelsData);
    } catch (error) {
      console.error('Erro ao carregar níveis:', error);
    }
  };

  const handleCheckRow = useCallback(
    async (id: string) => {
      const benefit = benefits.find((b) => b.id === id);
      if (benefit) {
        benefit.isActive = !benefit.isActive;
        setBenefits([...benefits]);

        if (benefit.isActive) {
          setChecked([...checked, id]);
        } else {
          setChecked(checked.filter((c) => c !== id));
        }
      }
    },
    [benefits, checked]
  );

  const createSortHandler = (id: string) => {
    if (orderBy === id) {
      if (order === 'asc') {
        setOrder('desc');
      } else {
        setOrder('asc');
      }
    } else if (order !== id) {
      setOrderBy(id);
      setOrder('asc');
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    const newBenefit = {
      id: '-',
      name: '',
      description: '',
      category: '',
      levels: [],
      type: '',
      vouchersQuantity: 0,
      stock: 0,
      status: 'active',
      storeEvent: '',
      store: null,
      event: null,
      restaurant: null,
      startDate: null,
      endDate: null,
      banner: '',
      thumb: null,
      howToUse: [''],
      conditions: '',
      discount: 0,
      voucherType: '',
      parkingPromoInfo: null,
      redemptionLimitPerUser: 1,
    };

    setBenefits([newBenefit, ...benefits]);
    setChecked([newBenefit.id, ...checked]);
  };

  const handleDelete = (id: string) => {
    setRowToDelete(id);
    useConfirmDeleteDialog.onTrue();
  };

  const handleChangeStatus = async (id: string) => {
    const repository = new BenefitsRepository(api);
    const response = await repository.changeStatus(id);
    if (response.isLeft()) {
      toastWarning('Erro ao alterar status!', { variant: 'error' });
    }
    if (checked.includes(id)) {
      setChecked(checked.filter((c) => c !== id));
      const updatedRow = { ...benefits.find((f) => f.id === id), status: 'inactive' };
      setBenefits([...benefits.filter((f) => f.id !== id), updatedRow]);
    } else {
      setChecked([...checked, id]);
      const updatedRow = { ...benefits.find((f) => f.id === id), status: 'active' };
      setBenefits([updatedRow, ...benefits.filter((f) => f.id !== id)]);
    }
  };

  const handleConfirmDelete = async () => {
    useConfirmDeleteDialog.onFalse();
    if (rowToDelete.name !== '') {
      const repository = new BenefitsRepository(api);
      const response = await repository.deleteBenefit(rowToDelete.id);
      if (response.isLeft()) {
        toastWarning(response.value.message, { variant: 'error' });
      } else {
        toastWarning('Removido com sucesso!', { variant: 'success' });
        getData();
      }
    } else {
      setBenefits((prev) => prev.filter((r) => r.id !== rowToDelete.id));
    }
  };

  return {
    tableHeader,
    benefits,
    isLoading,
    order,
    orderBy,
    checked,
    page,
    total,
    limit,
    useConfirmDeleteDialog,
    categoriesOptions,
    levelsOptions,
    storesOptions,
    categories,
    stores,
    restaurants,
    levels,
    expandedRowId,
    handleCheckRow,
    createSortHandler,
    handleChangePage,
    handleChangeRowsPerPage,
    handleAddNew,
    handleDelete,
    handleConfirmDelete,
    getData,
    getCategories,
    getStores,
    getRestaurants,
    handleChangeStatus,
    getLevels,
    toggleExpandRow,
  };
};
